import { FormEvent, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, CheckCircle, AlertCircle, Clock, XCircle } from 'lucide-react';
import { Card } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Switch } from '@/shared/components/ui/switch';
import { Textarea } from '@/shared/components/ui/textarea';
import { DisplayField } from './DisplayField';
import { useUpdateProfile } from '../../hooks/useUpdateProfile';
import { useToast } from '@/shared/components/Toast';
import type { ServiceProviderProfile } from '../../api/profileApi';

type ReadOnlyInformationTabProps = {
  profile: ServiceProviderProfile;
  /** Reports form dirty state so parents can guard against losing edits on tab switch. */
  onDirtyChange?: (dirty: boolean) => void;
};

type CollapsibleSectionProps = {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
};

type ProfileFormState = {
  tagline: string;
  providerBio: string;
  companyOverview: string;
  serviceOfferings: string;
  carePhilosophy: string;
  whyThisWork: string;
  targetAgeRange: string;
  priceRangeIndicator: string;
  priceDetailsText: string;
  hoursDescription: string;
  emergencyResponseAvailable: boolean;
  emergencyResponseDetails: string;
  phoneNumber: string;
  websiteUrl: string;
  languages: string;
  continuingEducation: string;
  newClientAcceptance: boolean;
};

const toFormState = (profile: ServiceProviderProfile): ProfileFormState => ({
  tagline: profile.tagline ?? '',
  providerBio: profile.providerBio ?? '',
  companyOverview: profile.companyOverview ?? '',
  serviceOfferings: profile.serviceOfferings ?? '',
  carePhilosophy: profile.carePhilosophy ?? '',
  whyThisWork: profile.whyThisWork ?? '',
  targetAgeRange: profile.targetAgeRange ?? '',
  priceRangeIndicator: profile.priceRangeIndicator ?? '',
  priceDetailsText: profile.priceDetailsText ?? '',
  hoursDescription: profile.hoursDescription ?? '',
  emergencyResponseAvailable: profile.emergencyResponseAvailable ?? false,
  emergencyResponseDetails: profile.emergencyResponseDetails ?? '',
  phoneNumber: profile.phoneNumber ?? '',
  websiteUrl: profile.websiteUrl ?? '',
  languages: (profile.languages ?? []).join(', '),
  continuingEducation: profile.continuingEducation ?? '',
  newClientAcceptance: profile.newClientAcceptance ?? true,
});

/** Human-readable labels for account/vetting status enums. */
const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  SUSPENDED: 'Suspended',
  PENDING_VERIFICATION: 'Pending Verification',
  APPROVED: 'Approved',
  PENDING: 'Pending',
  IN_REVIEW: 'In Review',
  REJECTED: 'Rejected',
};

const statusLabel = (status: string): string => STATUS_LABELS[status] ?? status;

function CollapsibleSection({ title, isOpen, onToggle, children }: CollapsibleSectionProps) {
  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full px-6 py-4 flex items-center justify-between bg-card-subtle transition-colors hover:bg-sage-bg"
      >
        <h3 className="text-lg font-medium text-text-primary">{title}</h3>
        {isOpen ? (
          <ChevronDown className="h-5 w-5 text-text-tertiary" />
        ) : (
          <ChevronRight className="h-5 w-5 text-text-tertiary" />
        )}
      </button>
      {isOpen && (
        <div className="px-6 py-4 space-y-4 border-t border-border-subtle">
          {children}
        </div>
      )}
    </Card>
  );
}

export function ReadOnlyInformationTab({ profile, onDirtyChange }: ReadOnlyInformationTabProps) {
  const [openSections, setOpenSections] = useState<Set<string>>(new Set(['account', 'basic']));
  const [formState, setFormState] = useState<ProfileFormState>(() => toFormState(profile));
  const [saveError, setSaveError] = useState('');
  const updateProfile = useUpdateProfile();
  const { showToast } = useToast();

  useEffect(() => {
    setFormState(toFormState(profile));
  }, [profile]);

  const isDirty = JSON.stringify(formState) !== JSON.stringify(toFormState(profile));

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Clear the parent's dirty flag when this form unmounts (edits are gone either way).
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  // Warn on page refresh/close while edits are unsaved.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const toggleSection = (sectionId: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const updateField = (field: keyof ProfileFormState, value: string | boolean) => {
    setFormState((current) => ({ ...current, [field]: value }));
    setSaveError('');
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaveError('');

    try {
      await updateProfile.mutateAsync({
        tagline: formState.tagline || null,
        providerBio: formState.providerBio || null,
        companyOverview: formState.companyOverview || null,
        serviceOfferings: formState.serviceOfferings || null,
        carePhilosophy: formState.carePhilosophy || null,
        whyThisWork: formState.whyThisWork || null,
        targetAgeRange: formState.targetAgeRange || null,
        priceRangeIndicator: formState.priceRangeIndicator || null,
        priceDetailsText: formState.priceDetailsText || null,
        hoursDescription: formState.hoursDescription || null,
        emergencyResponseAvailable: formState.emergencyResponseAvailable,
        emergencyResponseDetails: formState.emergencyResponseAvailable
          ? formState.emergencyResponseDetails || null
          : null,
        phoneNumber: formState.phoneNumber || null,
        websiteUrl: formState.websiteUrl || null,
        languages: formState.languages
          ? formState.languages.split(',').map((l) => l.trim()).filter(Boolean)
          : [],
        continuingEducation: formState.continuingEducation || null,
        newClientAcceptance: formState.newClientAcceptance,
      });
      showToast('Profile successfully updated!', 'success');
    } catch {
      setSaveError('Unable to save profile changes. Please try again.');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ACTIVE':
      case 'APPROVED':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'INACTIVE':
      case 'PENDING':
        return <Clock className="h-5 w-5 text-yellow-600" />;
      case 'IN_REVIEW':
        return <AlertCircle className="h-5 w-5 text-blue-600" />;
      case 'REJECTED':
      case 'SUSPENDED':
        return <XCircle className="h-5 w-5 text-red-600" />;
      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <CollapsibleSection
        title="Account Management"
        isOpen={openSections.has('account')}
        onToggle={() => toggleSection('account')}
      >
        <DisplayField label="Email" value={profile.user.email} />
        <DisplayField label="First Name" value={profile.user.firstName} />
        <DisplayField label="Last Name" value={profile.user.lastName} />
        <DisplayField
          label="Account Status"
          value={statusLabel(profile.user.accountStatus)}
          icon={getStatusIcon(profile.user.accountStatus)}
        />
      </CollapsibleSection>

      <CollapsibleSection
        title="Basic Information"
        isOpen={openSections.has('basic')}
        onToggle={() => toggleSection('basic')}
      >
        <DisplayField label="Company Name" value={profile.companyName} />
        <DisplayField label="Years in Business" value={profile.yearsInBusiness} />
        <DisplayField label="Established Year" value={profile.establishedYear} />
        <DisplayField
          label="Vetting Status"
          value={statusLabel(profile.vettingStatus)}
          icon={getStatusIcon(profile.vettingStatus)}
        />
        <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4">
          <div>
            <Label htmlFor="newClientAcceptance">Accepting New Clients</Label>
            <p className="text-sm text-gray-600">
              Toggle off to stop appearing in new client searches.
            </p>
          </div>
          <Switch
            id="newClientAcceptance"
            aria-label="Accepting New Clients"
            checked={formState.newClientAcceptance}
            onCheckedChange={(checked) => updateField('newClientAcceptance', checked)}
            disabled={updateProfile.isPending}
          />
        </div>
        <div className="pt-2">
          <label className="text-sm font-medium text-gray-700 block mb-2">Profile Visibility</label>
          <Badge
            className={
              profile.user.accountStatus === 'ACTIVE' && profile.vettingStatus === 'APPROVED'
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-700'
            }
          >
            {profile.user.accountStatus === 'ACTIVE' && profile.vettingStatus === 'APPROVED'
              ? 'Live - Visible to Clients'
              : 'Not Visible - Pending Approval'}
          </Badge>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Contact Details"
        isOpen={openSections.has('contact')}
        onToggle={() => toggleSection('contact')}
      >
        <div>
          <Label htmlFor="phoneNumber">Phone Number</Label>
          <Input
            id="phoneNumber"
            value={formState.phoneNumber}
            onChange={(e) => updateField('phoneNumber', e.target.value)}
            disabled={updateProfile.isPending}
          />
        </div>
        <div>
          <Label htmlFor="websiteUrl">Website URL</Label>
          <Input
            id="websiteUrl"
            value={formState.websiteUrl}
            onChange={(e) => updateField('websiteUrl', e.target.value)}
            disabled={updateProfile.isPending}
          />
        </div>
        <DisplayField label="Calendly URL" value={profile.calendlyUrl} />
        <DisplayField label="Business Address" value={profile.businessAddress} />
      </CollapsibleSection>

      <CollapsibleSection
        title="Profile Media"
        isOpen={openSections.has('media')}
        onToggle={() => toggleSection('media')}
      >
        {profile.heroMediaUrl && (
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">Hero Media</label>
            {profile.mediaType === 'video' ? (
              <video src={profile.heroMediaUrl} controls className="w-full rounded-lg max-w-2xl" />
            ) : (
              <img src={profile.heroMediaUrl} alt="Hero" className="w-full rounded-lg max-w-2xl" />
            )}
          </div>
        )}
        {profile.user.profilePhoto && (
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">Profile Photo</label>
            <img
              src={profile.user.profilePhoto}
              alt="Profile"
              className="w-32 h-32 rounded-full object-cover"
            />
          </div>
        )}
        {!profile.heroMediaUrl && !profile.user.profilePhoto && (
          <p className="text-gray-500 italic">No media uploaded</p>
        )}
      </CollapsibleSection>

      <CollapsibleSection
        title="Professional Credentials"
        isOpen={openSections.has('credentials')}
        onToggle={() => toggleSection('credentials')}
      >
        <DisplayField label="Years of Experience" value={profile.yearsOfExperience} />
        <DisplayField label="Professional Title" value={profile.professionalTitle} />
        {profile.certifications && profile.certifications.length > 0 && (
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">Certifications</label>
            <ul className="list-disc list-inside space-y-1">
              {profile.certifications.map((cert, index) => (
                <li key={index} className="text-gray-900">{cert}</li>
              ))}
            </ul>
          </div>
        )}
        {profile.professionalLicenses && profile.professionalLicenses.length > 0 && (
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">Professional Licenses</label>
            <ul className="list-disc list-inside space-y-1">
              {profile.professionalLicenses.map((license, index) => (
                <li key={index} className="text-gray-900">{license}</li>
              ))}
            </ul>
          </div>
        )}
      </CollapsibleSection>

      <CollapsibleSection
        title="Professional Identity"
        isOpen={openSections.has('identity')}
        onToggle={() => toggleSection('identity')}
      >
        <div>
          <Label htmlFor="serviceOfferings">Service Offerings</Label>
          <Textarea
            id="serviceOfferings"
            value={formState.serviceOfferings}
            onChange={(e) => updateField('serviceOfferings', e.target.value)}
            disabled={updateProfile.isPending}
          />
        </div>
        <div>
          <Label htmlFor="carePhilosophy">Care Philosophy</Label>
          <Textarea
            id="carePhilosophy"
            value={formState.carePhilosophy}
            onChange={(e) => updateField('carePhilosophy', e.target.value)}
            disabled={updateProfile.isPending}
          />
        </div>
        <div>
          <Label htmlFor="whyThisWork">Why This Work</Label>
          <Textarea
            id="whyThisWork"
            value={formState.whyThisWork}
            onChange={(e) => updateField('whyThisWork', e.target.value)}
            disabled={updateProfile.isPending}
          />
        </div>
        <div>
          <Label htmlFor="targetAgeRange">Target Age Range</Label>
          <Input
            id="targetAgeRange"
            value={formState.targetAgeRange}
            onChange={(e) => updateField('targetAgeRange', e.target.value)}
            disabled={updateProfile.isPending}
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Services & Pricing"
        isOpen={openSections.has('pricing')}
        onToggle={() => toggleSection('pricing')}
      >
        <div>
          <Label htmlFor="priceRangeIndicator">Price Range Indicator</Label>
          <select
            id="priceRangeIndicator"
            value={formState.priceRangeIndicator}
            onChange={(e) => updateField('priceRangeIndicator', e.target.value)}
            disabled={updateProfile.isPending}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="">Select price range</option>
            <option value="$">$ - Budget-friendly</option>
            <option value="$$">$$ - Moderate</option>
            <option value="$$$">$$$ - Premium</option>
            <option value="$$$$">$$$$ - Luxury</option>
          </select>
        </div>
        <div>
          <Label htmlFor="priceDetailsText">Price Details</Label>
          <Textarea
            id="priceDetailsText"
            value={formState.priceDetailsText}
            onChange={(e) => updateField('priceDetailsText', e.target.value)}
            disabled={updateProfile.isPending}
          />
        </div>
        <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4">
          <Label htmlFor="emergencyResponseAvailable">Emergency Response Available</Label>
          <Switch
            id="emergencyResponseAvailable"
            aria-label="Emergency Response Available"
            checked={formState.emergencyResponseAvailable}
            onCheckedChange={(checked) => updateField('emergencyResponseAvailable', checked)}
            disabled={updateProfile.isPending}
          />
        </div>
        {formState.emergencyResponseAvailable && (
          <div>
            <Label htmlFor="emergencyResponseDetails">Emergency Response Details</Label>
            <Textarea
              id="emergencyResponseDetails"
              value={formState.emergencyResponseDetails}
              onChange={(e) => updateField('emergencyResponseDetails', e.target.value)}
              disabled={updateProfile.isPending}
            />
          </div>
        )}
        <div>
          <Label htmlFor="hoursDescription">Hours Description</Label>
          <Textarea
            id="hoursDescription"
            value={formState.hoursDescription}
            onChange={(e) => updateField('hoursDescription', e.target.value)}
            disabled={updateProfile.isPending}
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Geographic Coverage"
        isOpen={openSections.has('geographic')}
        onToggle={() => toggleSection('geographic')}
      >
        <DisplayField label="National Coverage" value={profile.nationalCoverage} />
        {profile.registeredStates && profile.registeredStates.length > 0 && (
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">Registered States</label>
            <div className="flex flex-wrap gap-2">
              {profile.registeredStates.map((state, index) => (
                <Badge key={index} variant="outline">{state}</Badge>
              ))}
            </div>
          </div>
        )}
      </CollapsibleSection>

      <CollapsibleSection
        title="Categories, Specialties & Expertise"
        isOpen={openSections.has('taxonomies')}
        onToggle={() => toggleSection('taxonomies')}
      >
        {profile.categories && profile.categories.length > 0 && (
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">Categories</label>
            <div className="flex flex-wrap gap-2">
              {profile.categories.map((cat) => (
                <Badge key={cat.id} className="bg-blue-100 text-blue-700">{cat.name}</Badge>
              ))}
            </div>
          </div>
        )}
        {profile.specialties && profile.specialties.length > 0 && (
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">Specialties</label>
            <div className="flex flex-wrap gap-2">
              {profile.specialties.map((spec) => (
                <Badge key={spec.id} className="bg-purple-100 text-purple-700">{spec.name}</Badge>
              ))}
            </div>
          </div>
        )}
        {profile.expertise && profile.expertise.length > 0 && (
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">Expertise</label>
            <div className="flex flex-wrap gap-2">
              {profile.expertise.map((exp) => (
                <Badge key={exp.id} className="bg-green-100 text-green-700">{exp.name}</Badge>
              ))}
            </div>
          </div>
        )}
      </CollapsibleSection>

      <CollapsibleSection
        title="Additional Details"
        isOpen={openSections.has('additional')}
        onToggle={() => toggleSection('additional')}
      >
        <div>
          <Label htmlFor="tagline">Tagline</Label>
          <Input
            id="tagline"
            value={formState.tagline}
            onChange={(e) => updateField('tagline', e.target.value)}
            disabled={updateProfile.isPending}
          />
        </div>
        <div>
          <Label htmlFor="companyOverview">Company Overview</Label>
          <Textarea
            id="companyOverview"
            value={formState.companyOverview}
            onChange={(e) => updateField('companyOverview', e.target.value)}
            disabled={updateProfile.isPending}
          />
        </div>
        <div>
          <Label htmlFor="providerBio">Provider Bio</Label>
          <Textarea
            id="providerBio"
            value={formState.providerBio}
            onChange={(e) => updateField('providerBio', e.target.value)}
            disabled={updateProfile.isPending}
          />
        </div>
        <DisplayField label="Introductory Video URL" value={profile.introductoryVideoUrl} />
        <div>
          <Label htmlFor="languages">Languages</Label>
          <Input
            id="languages"
            value={formState.languages}
            onChange={(e) => updateField('languages', e.target.value)}
            placeholder="English, Spanish, French"
            disabled={updateProfile.isPending}
          />
          <p className="text-xs text-gray-500 mt-1">Separate multiple languages with commas.</p>
        </div>
        <div>
          <Label htmlFor="continuingEducation">Continuing Education</Label>
          <Textarea
            id="continuingEducation"
            value={formState.continuingEducation}
            onChange={(e) => updateField('continuingEducation', e.target.value)}
            disabled={updateProfile.isPending}
          />
        </div>
      </CollapsibleSection>

      {saveError && (
        <p className="text-sm text-red-600" role="alert">{saveError}</p>
      )}

      <Button type="submit" disabled={updateProfile.isPending || !isDirty}>
        {updateProfile.isPending ? 'Saving...' : 'Save Profile'}
      </Button>
    </form>
  );
}
